import {
  PdpmDerivation,
  PdpmEstimate,
  PdpmEstimateInput,
  PdpmInputOption,
  PdpmInputSection,
  PdpmWarning,
  PdpmWarningSeverity,
} from 'src/services/documentService/pdpmEstimate';

/**
 * BRK-1967: pure helpers for the PDPM estimate panel. Kept out of the component
 * so the label-parsing and mapping rules are testable on their own.
 */

/**
 * The advisory framing, shown wherever the estimate is. Lives here rather than in
 * the panel so both the panel and the drawer can use it without an import cycle.
 */
export const ADVISORY_CHIP_TEXT = 'Advisory — not an MDS submission or grouper output';

/** The sentinel first option in both CMG dropdowns — never a valid estimate target. */
export const NOT_APPLICABLE = 'Not Applicable';

/** The backend signal that this EOC is not therapy-driven, so the panel hides itself. */
export const NON_THERAPY_EOC = 'NON_THERAPY_EOC';

/**
 * Finds the facility's dropdown option for a CMG group code. The option labels
 * are built per-facility as `"TB - 300 mins"`, so an exact match handles a bare
 * code and the prefix match handles the labelled form. `Not Applicable` is never
 * resolvable in either direction — applying it would be a no-op that reads as a
 * successful apply.
 */
export const resolveCmgOption = (
  code: string | null | undefined,
  options: string[] | null | undefined
): string | null => {
  const trimmed = code?.trim();
  if (!trimmed || trimmed === NOT_APPLICABLE) {
    return null;
  }
  const match = (options || []).find(
    (option) =>
      typeof option === 'string' && (option === trimmed || option.startsWith(`${trimmed} `))
  );
  return match && match !== NOT_APPLICABLE ? match : null;
};

/**
 * Target minutes out of an option label (`"TB - 300 mins"` → `300`). Returns
 * null when there is no label or no minutes in it; 0 is returned as 0 and means
 * "not configured" rather than "zero minutes" — see `formatTargetMinutes`.
 */
export const parseTargetMinutes = (option: string | null | undefined): number | null => {
  if (!option) {
    return null;
  }
  const match = /-\s*(\d+)\s*mins/i.exec(option);
  if (!match) {
    return null;
  }
  const minutes = Number(match[1]);
  return Number.isFinite(minutes) ? minutes : null;
};

/**
 * Display string for a resolved option's minutes. The form coerces a group with
 * no configured minutes to `'0'` (therapyPlanningEditDetails L260), so 0 is an
 * unconfigured facility catalog entry, not a real zero-minute target.
 */
export const formatTargetMinutes = (option: string | null | undefined): string | null => {
  const minutes = parseTargetMinutes(option);
  if (minutes === null) {
    return null;
  }
  return minutes === 0 ? 'Target not configured' : `${minutes} mins target`;
};

/**
 * Structurally MUI's `AlertColor`, declared locally: the `import/named` lint rule
 * cannot resolve MUI's type-only exports, and this union is the whole surface.
 */
export type MuiAlertSeverity = 'error' | 'warning' | 'info' | 'success';

/** Contract severity → MUI Alert severity. Unknown values degrade to info. */
export const warningSeverity = (
  severity: PdpmWarningSeverity | null | undefined
): MuiAlertSeverity => {
  switch (severity) {
    case 'ERROR':
      return 'error';
    case 'WARN':
      return 'warning';
    case 'INFO':
      return 'info';
    default:
      return 'info';
  }
};

const SEVERITY_RANK: Record<string, number> = { ERROR: 0, WARN: 1, INFO: 2 };

/**
 * Warnings worst-first so a truncated list always shows the most serious ones.
 * Sorted on a copy — the estimate payload is rendered elsewhere and must not be
 * reordered underneath it.
 */
export const sortWarnings = (warnings: PdpmWarning[] | null | undefined): PdpmWarning[] =>
  [...(warnings || [])].sort(
    (a, b) => (SEVERITY_RANK[a?.severity] ?? 3) - (SEVERITY_RANK[b?.severity] ?? 3)
  );

/** True when the backend says this EOC is not therapy-driven. */
export const hasNonTherapyEoc = (estimate: PdpmEstimate | null | undefined): boolean =>
  (estimate?.warnings || []).some((warning) => warning?.code === NON_THERAPY_EOC);

/**
 * True when the engine could not produce groups. Both disciplines come back null
 * together on an INSUFFICIENT payload; the panel then shows the remediation
 * state instead of empty tiles.
 */
export const isSuppressed = (estimate: PdpmEstimate | null | undefined): boolean =>
  !!estimate && !estimate.ptOtCmg && !estimate.slpCmg;

export type PdpmSourceChip = {
  label: string;
  color: 'default' | 'info' | 'warning';
  variant: 'filled' | 'outlined';
};

/**
 * Source chip for an input row (consumed by the FE-3 drawer). An AUTO row names
 * the document it was pulled from so the value is traceable; MISSING is styled
 * as a warning because a missing counted input degrades the estimate.
 */
export const sourceChip = (
  input: Pick<PdpmEstimateInput, 'source' | 'evidence'>
): PdpmSourceChip => {
  switch (input?.source) {
    case 'OVERRIDE':
      return { label: 'Override', color: 'info', variant: 'filled' };
    case 'MISSING':
      return { label: 'Missing', color: 'warning', variant: 'filled' };
    case 'ANALYTICS_FALLBACK':
      return { label: 'Analytics fallback', color: 'default', variant: 'outlined' };
    case 'DERIVED':
      return { label: 'Derived', color: 'default', variant: 'outlined' };
    case 'AUTO': {
      const parts = ['Auto', input.evidence?.documentType, input.evidence?.dateOfService].filter(
        Boolean
      );
      return { label: parts.join(' · '), color: 'default', variant: 'outlined' };
    }
    default:
      return { label: 'Unknown', color: 'default', variant: 'outlined' };
  }
};

/** "12 of 17 inputs documented", or null when the payload omits completeness. */
export const formatCompleteness = (
  completeness: { present: number; total: number } | null | undefined
): string | null =>
  completeness ? `${completeness.present} of ${completeness.total} inputs documented` : null;

/* ------------------------------------------------------------------ *
 * BRK-1968: inputs drawer                                            *
 * ------------------------------------------------------------------ */

/** The value cell for a row with nothing documented. */
export const EM_DASH = '—';

export const SECTION_TITLES: Record<PdpmInputSection, string> = {
  CLINICAL_CATEGORY: 'Clinical Category',
  FUNCTION_SCORE: 'Function Score',
  SLP: 'SLP',
};

export const SECTION_ORDER: PdpmInputSection[] = ['CLINICAL_CATEGORY', 'FUNCTION_SCORE', 'SLP'];

/**
 * Which section a row renders under. The backend's `section` wins; the itemId
 * fallback only covers the roster we know about today. Anything unrecognized
 * falls through to `null` and the drawer renders it in an "Other inputs" group —
 * the drawer is a renderer of whatever the endpoint sends, so a newly added
 * input must always appear somewhere rather than vanish.
 */
export const sectionForInput = (input: PdpmEstimateInput): PdpmInputSection | null => {
  const declared = input?.section;
  if (declared && declared in SECTION_TITLES) {
    return declared as PdpmInputSection;
  }
  const itemId = input?.itemId || '';
  if (itemId.startsWith('GG')) {
    return 'FUNCTION_SCORE';
  }
  if (
    itemId.startsWith('COMORBIDITY_') ||
    [
      'C0500',
      'COGNITIVE_IMPAIRMENT',
      'SWALLOWING_DISORDER',
      'MECH_ALTERED_DIET',
      'K0520C3',
    ].includes(itemId) ||
    /^K0100[A-D]$/.test(itemId)
  ) {
    return 'SLP';
  }
  if (['PRIMARY_DX', 'SURGICAL_HISTORY', 'CLINICAL_CATEGORY'].includes(itemId)) {
    return 'CLINICAL_CATEGORY';
  }
  return null;
};

export type GroupedInputs = {
  sections: { section: PdpmInputSection; title: string; inputs: PdpmEstimateInput[] }[];
  /** rows the FE could not place — rendered under "Other inputs", never dropped */
  other: PdpmEstimateInput[];
};

export const groupInputsBySection = (
  inputs: PdpmEstimateInput[] | null | undefined
): GroupedInputs => {
  const buckets = new Map<PdpmInputSection, PdpmEstimateInput[]>();
  const other: PdpmEstimateInput[] = [];
  (inputs || []).forEach((input) => {
    const section = sectionForInput(input);
    if (!section) {
      other.push(input);
      return;
    }
    const bucket = buckets.get(section) || [];
    bucket.push(input);
    buckets.set(section, bucket);
  });
  return {
    sections: SECTION_ORDER.filter((section) => (buckets.get(section) || []).length > 0).map(
      (section) => ({
        section,
        title: SECTION_TITLES[section],
        inputs: buckets.get(section) || [],
      })
    ),
    other,
  };
};

/** Rows in a bucket, in the order `derivation.buckets[].itemIds` lists them. */
export const inputsForBucket = (
  itemIds: string[] | null | undefined,
  inputs: PdpmEstimateInput[] | null | undefined
): PdpmEstimateInput[] => {
  const byId = new Map((inputs || []).map((input) => [input.itemId, input]));
  return (itemIds || [])
    .map((itemId) => byId.get(itemId))
    .filter((input): input is PdpmEstimateInput => !!input);
};

/**
 * The value cell. A MISSING row shows an em-dash rather than its raw value —
 * "documented as dependent" and "not documented" must never look the same.
 */
export const displayValue = (input: PdpmEstimateInput): string => {
  if (input?.source === 'MISSING') {
    return EM_DASH;
  }
  const value = input?.value;
  return value === null || value === undefined || value === '' ? EM_DASH : value;
};

/**
 * The points cell. Same rule as `displayValue`, and for the same reason: 0 points
 * is a real GG score, so a missing item must never render as 0.
 */
export const formatPoints = (input: PdpmEstimateInput): string => {
  if (input?.source === 'MISSING' || input?.points === null || input?.points === undefined) {
    return EM_DASH;
  }
  return String(input.points);
};

/**
 * "1 point from TC" — computed from the backend's `nextBand`, never from an FE
 * band table (the bands are fiscal-year data and would silently go stale here).
 */
export const proximityHint = (derivation: PdpmDerivation | null | undefined): string | null => {
  const nextBand = derivation?.nextBand;
  const score = derivation?.functionScore;
  if (!nextBand?.group || score === null || score === undefined) {
    return null;
  }
  const gap = nextBand.atScore - score;
  if (gap <= 0) {
    return null;
  }
  return `${gap} ${gap === 1 ? 'point' : 'points'} from ${nextBand.group}`;
};

/** Warnings scoped to one row, rendered inline beside it. */
export const warningsForItem = (
  warnings: PdpmWarning[] | null | undefined,
  itemId: string
): PdpmWarning[] => (warnings || []).filter((warning) => warning?.itemId === itemId);

/** Warnings with no itemId — they belong to the estimate, not to a row. */
export const generalWarnings = (warnings: PdpmWarning[] | null | undefined): PdpmWarning[] =>
  (warnings || []).filter((warning) => !warning?.itemId);

export const overrideCount = (overrides: Record<string, string> | null | undefined): number =>
  Object.keys(overrides || {}).length;

/**
 * BIMS is serialized as a zero-padded string in `mds_item_collected`, so the
 * editor's values must be '00'-'15' plus '99' (unknown) — not numbers.
 */
export const bimsOptions = (): PdpmInputOption[] => [
  ...Array.from({ length: 16 }, (_, index) => {
    const value = String(index).padStart(2, '0');
    return { value, description: value };
  }),
  { value: '99', description: '99 — unknown' },
];
